<script>
import { onMount } from 'svelte';
import { formatDistance, addSeconds, endOfSecond, addMinutes, startOfSecond } from 'date-fns';
import { minute } from '../data/datetime';
import { timeLeft, sprintTiming, secondsLeft, secondsUntil, status, percent } from '../data/sprints';

export let size = 220;
export let stroke = 40;
const padding = 2;
const SOUND_START = 10;
let sound;

$: radius = (size - padding * 2) / 2;
$: normalizedRadius = radius - stroke / 2;
$: circumference = normalizedRadius * 2 * Math.PI;
$: offset = circumference - $percent * circumference;

$: minutes = Math.floor(($secondsUntil || $secondsLeft) / 60);
$: seconds = Math.ceil($secondsUntil || $secondsLeft) % 60;
$: content = (minutes ? minutes + ':' : '') + (minutes && seconds < 10 ? '0' : '') + seconds;
$: final = !minutes && seconds <= 10 && $timeLeft > 0;
$: even = final && seconds % 2 === 0;
$: playSound($secondsUntil || $secondsLeft);

onMount(() => {
  sound.load();
});

function playSound(left) {
  if (left <= SOUND_START && left > 0 && sound && sound.readyState === 4 && sound.paused) {
    sound.play().then(() => sound.currentTime = (SOUND_START - left)).catch(() => {});
  }
}
</script>

<div class="sprint-timer">
  <svg width="{size}px" height="{size}px" viewBox="0 0 {size} {size}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <defs>
      <circle id="background-ring" fill="transparent" stroke-width={stroke} r={normalizedRadius} cx={radius + padding} cy={radius + padding}/>
      <circle id="progress-ring" fill="transparent" stroke-dasharray="{circumference} {circumference}" style="stroke-dashoffset: {offset}" stroke-width={stroke + 1} r={normalizedRadius} cx={radius + padding} cy={radius + padding}/>
      <filter id="background-filter" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1.5" in="SourceAlpha" result="shadowBlurInner"></feGaussianBlur>
        <feOffset dx="0" dy="1" in="shadowBlurInner" result="shadowOffsetInner"></feOffset>
        <feComposite in="shadowOffsetInner" in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="shadowInnerInner1"></feComposite>
        <feColorMatrix values="0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.3 0" type="matrix" in="shadowInnerInner1"></feColorMatrix>
      </filter>
      <filter id="progress-filter" x="-50%" y="-50%" width="200%" height="200%">
        <feOffset dx="0" dy="1" in="SourceAlpha" result="shadowOffsetOuter1"></feOffset>
        <feGaussianBlur stdDeviation="1" in="shadowOffsetOuter1" result="shadowBlurOuter1"></feGaussianBlur>
        <feColorMatrix values="0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.3 0" type="matrix" in="shadowBlurOuter1"></feColorMatrix>
      </filter>
    </defs>
    <g>
      <use stroke="var(--cyan-700)" fill-rule="evenodd" xlink:href="#background-ring"></use>
      <use stroke="black" filter="url(#background-filter)" xlink:href="#background-ring"></use>
    </g>
    <g>
      <use stroke="black" filter="url(#progress-filter)" xlink:href="#progress-ring"></use>
      <use stroke="var(--cyan-300)" fill-rule="evenodd" xlink:href="#progress-ring"></use>
    </g>
  </svg>
  <div class="time {$status}">
    <div class="time-display">
      {#if $status === 'started'}
      <div class="units {minutes > 9 ? 'small' : minutes ? 'medium' : seconds > 9 ? 'large' : 'xl'}"
        class:final
        class:even={final && even}
        class:odd={final && !even}
        data-content={content}
      >
        {content}
      </div>
      <div class="label">{minutes ? 'minutes' : 'seconds'}</div>
      {:else if $status === 'finished'}
      <div class="done">Finished</div>
      {:else if $secondsUntil > 900}
      <div class="label">starting in</div>
      <div class="time-to-start">{formatDistance($minute, $sprintTiming.start)}</div>
      {:else}
      <div class="label">starting in</div>
      <div class="units {minutes > 9 ? 'small' : minutes ? 'medium' : seconds > 9 ? 'large' : 'xl'}"
        class:final
        class:even={final && even}
        class:odd={final && !even}
        data-content={content}
      >
        {content}
      </div>
      {/if}
    </div>
  </div>
  <audio bind:this={sound}><source src="/10-second-countdown.mp3" type="audio/mpeg" /></audio>
</div>

<style>
.sprint-timer {
  position: relative;
  width: 220px;
}
.time {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.time-display {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.units {
  font-family: var(--font-header);
  font-size: var(--font-size-700);
  font-weight: 700;
  color: var(--warm-grey-600);
  line-height: .8;
}
.time.pending .units {
  line-height: 1;
}
.medium {
  font-size: var(--font-size-800);
}
.large {
  font-size: var(--font-size-900);
}
.xl {
  font-size: var(--font-size-950);
}
.final, .xl {
  color: var(--cyan-300);
  text-shadow: 0 1px 1px rgba(0, 0, 0, .3);
}
.done {
  font-family: var(--font-header);
  font-size: var(--font-size-500);
  font-weight: 700;
  text-transform: uppercase;
  color: var(--warm-grey-600);
}
.label {
  align-self: flex-end;
  font-size: var(--font-size-050);
  color: var(--warm-grey-400);
}
.time-to-start {
  font-weight: 700;
  font-size: var(--font-size-200);
  color: var(--warm-grey-500);
}
#progress-ring {
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}
.final.even::before, .final.odd::before {
  position: absolute;
  content: attr(data-content);
}
.final.even::before {
  animation: expload 1s ease-out;
}
.final.odd::before {
  animation: expload2 1s ease-out;
}
@keyframes expload {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(10);
    opacity: 0;
  }
}
@keyframes expload2 {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(10);
    opacity: 0;
  }
}
</style>
